import { maGocTuHangHoa } from '@/utils/maHangHienThi'

/** Các loại phiếu nhập/xuất kho (generic backend `/phieu-nhap-xuat-kho`). */
export type LoaiPhieuKho =
  | 'NHAP_MUA'
  | 'NHAP_CHUYEN'
  | 'NHAP_TRA_HANG'
  | 'NHAP_DIEU_CHINH'
  | 'NHAP_KHAC'
  | 'XUAT_BAN'
  | 'XUAT_CHUYEN'
  | 'XUAT_TRA_NCC'
  | 'XUAT_HUY'
  | 'XUAT_NOI_BO'
  | 'XUAT_DIEU_CHINH'
  | 'XUAT_KHAC'
  | 'KIEM_KE'

/** Hướng phiếu — NHAP / XUAT / KIEM_KE (PKCT có dấu). */
export type Huong = 'NHAP' | 'XUAT' | 'KIEM_KE'

/** Trạng thái phiếu kho. */
export type PhieuKhoTrangThai = 'phieuTam' | 'daHoanThanh' | 'daHuy'

/** Loại phiếu nhập chọn được khi tạo/sửa/lọc thủ công. Không gồm NHAP_DIEU_CHINH (chỉ legacy KK). */
export const LOAI_PHIEU_NHAP: LoaiPhieuKho[] = [
  'NHAP_MUA',
  'NHAP_CHUYEN',
  'NHAP_TRA_HANG',
  'NHAP_KHAC',
]

/**
 * Loại được chọn khi tạo/import thủ công.
 * `NHAP_TRA_HANG` chỉ tạo từ phiếu trả hàng (cần cả giá vốn + giá bán).
 */
export const LOAI_PHIEU_NHAP_TAO_MOI: LoaiPhieuKho[] = LOAI_PHIEU_NHAP.filter(
  (l) => l !== 'NHAP_TRA_HANG',
)

/** Loại phiếu xuất chọn được khi tạo/sửa/lọc thủ công. Không gồm XUAT_DIEU_CHINH (chỉ legacy KK). */
export const LOAI_PHIEU_XUAT: LoaiPhieuKho[] = [
  'XUAT_BAN',
  'XUAT_CHUYEN',
  'XUAT_TRA_NCC',
  'XUAT_HUY',
  'XUAT_NOI_BO',
  'XUAT_KHAC',
]

export function loaiPhieuToHuong(loai: LoaiPhieuKho): Huong {
  if (loai === 'KIEM_KE') return 'KIEM_KE'
  return loai.startsWith('NHAP_') ? 'NHAP' : 'XUAT'
}

/** Chỉ phiếu tạm (draft) mới được sửa/xóa — khớp logic backend. */
export function isPhieuKhoKhoaTheoTrangThai(t: PhieuKhoTrangThai | string | null | undefined): boolean {
  return t !== 'phieuTam'
}

/** Dòng chi tiết hàng hóa trong phiếu (PKCT). */
export type IPKCT = {
  id?: string
  phieuId?: string
  hangHoaId: string
  soLuong: number
  donGia: number
  giaVon?: number
  giamGia?: number
  thanhTien: number
  hinhThucVc?: string | null
  cont?: string | null
  maVanChuyenNcc?: string | null
  maVanChuyenAzl?: string | null
  maYcmh?: string | null
  maBaoGia?: string | null
  donViTinhMua?: string | null
  maLo?: string | null
  hanSuDung?: string | null
  serialImei?: string | null
  dateSanPham?: string | null
  ghiChu?: string | null
  thuTu?: number
  // Snapshot / convenience hiển thị
  maHangHoa?: string | null
  /** Mã gốc để hiển thị (hàng cùng loại `<maGoc>-n`); không dùng cho payload. */
  hangHoaGocMa?: string | null
  tenHangHoa?: string | null
  quanLyTheoHanSuDung?: boolean
  quanLySerialIMEI?: boolean
}

/** Header phiếu nhập/xuất kho (PKCH). */
export type IPKCH = {
  id: string
  maPhieu: string
  loaiPhieu: LoaiPhieuKho
  huong: Huong
  lyDo?: string | null
  trangThai: PhieuKhoTrangThai
  trangThaiRaw: string | null
  ngayChungTu: string
  nhaCungCapId?: string | null
  tenNhaCungCap?: string | null
  khachHangId?: string | null
  tenKhachHang?: string | null
  chiNhanhId?: string | null
  tenChiNhanh?: string | null
  chiNhanhDoiId?: string | null
  tenChiNhanhDoi?: string | null
  hoaDonId?: string | null
  phieuChuyenId?: string | null
  datHangNCCId?: string | null
  phieuNhanHangId?: string | null
  phieuTraId?: string | null
  phieuTra?: { id: string; maPhieuTraHang: string; trangThai?: string } | null
  phieuKiemKeId?: string | null
  yeuCauSuaChuaId?: string | null
  soLuongMatHang: number
  tongSoLuong: number
  tongTienHang: number
  giamGia?: number
  ghiChu?: string | null
  ngayTao?: string | null
  ngayCapNhat?: string | null
  nguoiThucHienId?: string | null
  chiTiet: IPKCT[]
}

/**
 * Prefill form tạo mới từ phiếu kho có sẵn (Sao chép).
 * Reset mã / trạng thái / liên kết chứng từ nguồn; giữ loại, NCC/KH, dòng hàng.
 */
export function buildPhieuKhoCopyInitial(src: IPKCH): IPKCH {
  return {
    ...src,
    id: '',
    maPhieu: '',
    trangThai: 'phieuTam',
    trangThaiRaw: null,
    ngayChungTu: '',
    hoaDonId: null,
    phieuChuyenId: null,
    datHangNCCId: null,
    phieuNhanHangId: null,
    phieuTraId: null,
    phieuTra: null,
    phieuKiemKeId: null,
    yeuCauSuaChuaId: null,
    ngayTao: null,
    ngayCapNhat: null,
    nguoiThucHienId: null,
    chiTiet: (src.chiTiet ?? []).map((line) => {
      const rest = { ...line }
      delete rest.id
      delete rest.phieuId
      return rest
    }),
  }
}

/** Payload dòng hàng hóa khi tạo/cập nhật phiếu. */
export type IPKCTPayload = {
  hangHoaId: string
  maHangHoa: string
  tenHangHoa: string
  soLuong: number
  donViTinhMua: string
  donGia: number
  giaVon?: number
  giamGia?: number
  hinhThucVc?: string
  cont?: string
  maVanChuyenNcc?: string
  maVanChuyenAzl?: string
  maYcmh?: string
  maBaoGia?: string
  maLo?: string
  hanSuDung?: string
  serialImei?: string
  dateSanPham?: string
  ghiChu?: string
}

function parseNum(raw: unknown): number | null {
  if (raw === '' || raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

function normalizeTrangThai(raw: unknown): {
  trangThai: PhieuKhoTrangThai
  trangThaiRaw: string | null
} {
  const s0 = raw != null ? String(raw) : ''
  const s = s0.trim()
  if (!s) return { trangThai: 'phieuTam', trangThaiRaw: null }
  const u = s.toUpperCase()
  if (u === 'PHIEUTAM' || u === 'PHIEU_TAM') return { trangThai: 'phieuTam', trangThaiRaw: s0 }
  if (u === 'DAHOANTHANH' || u === 'DA_HOAN_THANH' || u === 'HOANTHANH') {
    return { trangThai: 'daHoanThanh', trangThaiRaw: s0 }
  }
  if (u === 'DAHUY' || u === 'DA_HUY' || u === 'HUY') return { trangThai: 'daHuy', trangThaiRaw: s0 }
  if (u.includes('HUY') || u.includes('CANCEL')) return { trangThai: 'daHuy', trangThaiRaw: s0 }
  if (u.includes('HOAN') && u.includes('THANH')) return { trangThai: 'daHoanThanh', trangThaiRaw: s0 }
  return { trangThai: 'phieuTam', trangThaiRaw: s0 }
}

function mapChiTiet(raw: Record<string, unknown>): IPKCT {
  const hk = raw.hangHoa
  const hkO = hk && typeof hk === 'object' ? (hk as Record<string, unknown>) : undefined
  const soLuong = parseNum(raw.soLuong) ?? 0
  const donGia = parseNum(raw.donGia) ?? 0
  const giamGia = parseNum(raw.giamGia) ?? 0
  const strOrNull = (v: unknown) => (v != null && String(v).trim() ? String(v) : null)
  return {
    id: raw.id != null ? String(raw.id) : undefined,
    phieuId: raw.phieuId != null ? String(raw.phieuId) : undefined,
    hangHoaId: String(raw.hangHoaId ?? hkO?.id ?? ''),
    soLuong,
    donGia,
    giaVon: parseNum(raw.giaVon) ?? 0,
    giamGia,
    thanhTien:
      parseNum(raw.thanhTien) ?? soLuong * Math.max(0, donGia - giamGia),
    hinhThucVc: strOrNull(raw.hinhThucVc),
    cont: strOrNull(raw.cont),
    maVanChuyenNcc: strOrNull(raw.maVanChuyenNcc),
    maVanChuyenAzl: strOrNull(raw.maVanChuyenAzl),
    maYcmh: strOrNull(raw.maYcmh),
    maBaoGia: strOrNull(raw.maBaoGia),
    donViTinhMua: strOrNull(raw.donViTinhMua),
    maLo: raw.maLo != null ? String(raw.maLo) : null,
    hanSuDung: raw.hanSuDung != null ? String(raw.hanSuDung) : null,
    serialImei:
      strOrNull(raw.serialImei) ??
      (raw.maLo != null ? String(raw.maLo) : null),
    dateSanPham:
      raw.dateSanPham != null
        ? String(raw.dateSanPham)
        : raw.hanSuDung != null
          ? String(raw.hanSuDung)
          : null,
    ghiChu: raw.ghiChu != null ? String(raw.ghiChu) : null,
    thuTu: parseNum(raw.thuTu) ?? undefined,
    maHangHoa:
      strOrNull(raw.maHangHoa) ??
      (hkO != null ? strOrNull(hkO.maHangHoa) : null),
    hangHoaGocMa: maGocTuHangHoa(hkO),
    tenHangHoa:
      strOrNull(raw.tenHangHoa) ??
      (hkO != null ? strOrNull(hkO.tenHangHoa) : null),
    quanLyTheoHanSuDung:
      hkO != null && hkO.quanLyTheoHanSuDung != null
        ? Boolean(hkO.quanLyTheoHanSuDung)
        : raw.quanLyTheoHanSuDung != null
          ? Boolean(raw.quanLyTheoHanSuDung)
          : false,
    quanLySerialIMEI:
      hkO != null && hkO.quanLySerialIMEI != null
        ? Boolean(hkO.quanLySerialIMEI)
        : raw.quanLySerialIMEI != null
          ? Boolean(raw.quanLySerialIMEI)
          : false,
  }
}

function mapChiTiets(raw: unknown): IPKCT[] {
  if (!Array.isArray(raw)) return []
  const mapped = raw.map((r) =>
    mapChiTiet(r && typeof r === 'object' ? (r as Record<string, unknown>) : {}),
  )
  return mapped
    .map((item, idx) => ({ item, idx }))
    .sort((a, b) => {
      const ta = a.item.thuTu
      const tb = b.item.thuTu
      const aHas = ta != null && Number.isFinite(ta)
      const bHas = tb != null && Number.isFinite(tb)
      if (aHas && bHas && ta !== tb) return (ta as number) - (tb as number)
      if (aHas && !bHas) return -1
      if (!aHas && bHas) return 1
      return a.idx - b.idx
    })
    .map((x) => x.item)
}

function nestedTen(raw: Record<string, unknown>, key: string, nestedKey: string, tenField: string): string | null {
  const direct = raw[key]
  if (direct != null && String(direct).trim()) return String(direct)
  const nested = raw[nestedKey]
  if (nested && typeof nested === 'object') {
    const o = nested as Record<string, unknown>
    const t = o[tenField]
    if (t != null && String(t).trim()) return String(t)
  }
  return null
}

/** Mapper PKCH từ response backend → `IPKCH`. */
export function mapPhieuKhoFromApi(raw: Record<string, unknown>): IPKCH {
  const { trangThai, trangThaiRaw } = normalizeTrangThai(raw.trangThai)
  const loaiRaw = raw.loaiPhieu != null ? String(raw.loaiPhieu) : ''
  const loaiPhieu = (loaiRaw || 'NHAP_MUA') as LoaiPhieuKho
  const huongRaw = raw.huong != null ? String(raw.huong).toUpperCase() : ''
  const huong = (
    huongRaw === 'KIEM_KE' || loaiPhieu === 'KIEM_KE'
      ? 'KIEM_KE'
      : huongRaw === 'XUAT' || loaiPhieu.startsWith('XUAT_')
        ? 'XUAT'
        : 'NHAP'
  ) as Huong
  return {
    id: String(raw.id ?? ''),
    maPhieu: String(raw.maPhieu ?? ''),
    loaiPhieu,
    huong,
    lyDo: raw.lyDo != null ? String(raw.lyDo) : null,
    trangThai,
    trangThaiRaw,
    // Giữ full ISO/datetime — slice(0,10) làm mất giờ → FE hiện 07:00 VN (midnight UTC).
    ngayChungTu: String(raw.ngayChungTu ?? ''),
    nhaCungCapId:
      raw.nhaCungCapId != null && String(raw.nhaCungCapId) !== '' ? String(raw.nhaCungCapId) : null,
    tenNhaCungCap: nestedTen(raw, 'tenNhaCungCap', 'nhaCungCap', 'tenNhaCungCap'),
    khachHangId:
      raw.khachHangId != null && String(raw.khachHangId) !== '' ? String(raw.khachHangId) : null,
    tenKhachHang: nestedTen(raw, 'tenKhachHang', 'khachHang', 'tenKhachHang'),
    chiNhanhId:
      raw.chiNhanhId != null && String(raw.chiNhanhId) !== '' ? String(raw.chiNhanhId) : null,
    tenChiNhanh: nestedTen(raw, 'tenChiNhanh', 'chiNhanh', 'ten'),
    chiNhanhDoiId:
      raw.chiNhanhDoiId != null && String(raw.chiNhanhDoiId) !== ''
        ? String(raw.chiNhanhDoiId)
        : null,
    tenChiNhanhDoi: nestedTen(raw, 'tenChiNhanhDoi', 'chiNhanhDoi', 'ten'),
    hoaDonId: raw.hoaDonId != null && String(raw.hoaDonId) !== '' ? String(raw.hoaDonId) : null,
    phieuChuyenId:
      raw.phieuChuyenId != null && String(raw.phieuChuyenId) !== '' ? String(raw.phieuChuyenId) : null,
    datHangNCCId:
      raw.datHangNCCId != null && String(raw.datHangNCCId) !== '' ? String(raw.datHangNCCId) : null,
    phieuNhanHangId:
      raw.phieuNhanHangId != null && String(raw.phieuNhanHangId) !== ''
        ? String(raw.phieuNhanHangId)
        : null,
    phieuTraId:
      raw.phieuTraId != null && String(raw.phieuTraId) !== '' ? String(raw.phieuTraId) : null,
    phieuTra: (() => {
      const pt = raw.phieuTra
      if (!pt || typeof pt !== 'object') return null
      const o = pt as Record<string, unknown>
      const id = o.id != null ? String(o.id) : ''
      const maPhieuTraHang = o.maPhieuTraHang != null ? String(o.maPhieuTraHang) : ''
      if (!id || !maPhieuTraHang) return null
      const trangThai = o.trangThai != null ? String(o.trangThai) : undefined
      return { id, maPhieuTraHang, ...(trangThai ? { trangThai } : {}) }
    })(),
    phieuKiemKeId:
      raw.phieuKiemKeId != null && String(raw.phieuKiemKeId) !== '' ? String(raw.phieuKiemKeId) : null,
    soLuongMatHang: parseNum(raw.soLuongMatHang) ?? 0,
    tongSoLuong: parseNum(raw.tongSoLuong) ?? 0,
    tongTienHang: parseNum(raw.tongTienHang) ?? parseNum(raw.tongTien) ?? 0,
    giamGia: parseNum(raw.giamGia) ?? 0,
    ghiChu: raw.ghiChu != null ? String(raw.ghiChu) : null,
    ngayTao: raw.ngayTao != null ? String(raw.ngayTao) : null,
    ngayCapNhat: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : null,
    nguoiThucHienId:
      raw.nguoiThucHienId != null && String(raw.nguoiThucHienId) !== ''
        ? String(raw.nguoiThucHienId)
        : null,
    chiTiet: mapChiTiets(raw.chiTiet ?? raw.danhSachHangHoa),
  }
}
